import { useMemo } from "react";

import { CompareDatePicker, DatePicker, Select } from "@components/v2";
import { minDateForTimeGranularity } from "@features/sales-v4/utils";
import { type User } from "@internal/user";
import {
  dateRangeToInterval,
  parseDateInterval,
  TimeGranularityV2,
  todayLocalAsISODate,
  type CompareDateRangeV2,
  type DateRangeChoiceV4,
  type FixedDateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

interface Props {
  dateRange: FixedDateInterval | DateRangeChoiceV4;
  compareDateRange: CompareDateRangeV2;
  timeGranularity: ITimeGranularityV2;
  onChangeDateRange: (val: FixedDateInterval | DateRangeChoiceV4) => void;
  onChangeCompareDateRange: (val: CompareDateRangeV2) => void;
  onChangeTimeGranularity: (val: ITimeGranularityV2) => void;
  timezone: User["timezone"];
}

export const Dates = ({
  dateRange,
  compareDateRange,
  timeGranularity,
  onChangeDateRange,
  onChangeCompareDateRange,
  onChangeTimeGranularity,
  timezone,
}: Props) => {
  const { minDate, minCompareDate } =
    minDateForTimeGranularity(timeGranularity);

  const referenceDateRange = useMemo(() => {
    if (typeof dateRange === "string") {
      return parseDateInterval(
        dateRangeToInterval(dateRange),
        todayLocalAsISODate(),
      );
    }

    return { startDate: dateRange.startDate, endDate: dateRange.endDate };
  }, [dateRange]);

  return (
    <div className="flex items-center gap-2">
      <div>
        <div className="text-xs font-semibold mb-2">Dates</div>
        <DatePicker
          className="w-48"
          minDate={minDate}
          value={dateRange}
          onChange={(val) => val && onChangeDateRange(val)}
        />
      </div>
      <div>
        <div className="text-xs font-semibold mb-2">Comparison Period</div>
        <CompareDatePicker
          className="w-48"
          minDate={minCompareDate}
          value={compareDateRange}
          onChange={(val) => val && onChangeCompareDateRange(val)}
          referenceDateRange={referenceDateRange}
          timezone={timezone}
          timeGranularity={timeGranularity}
        />
      </div>
      <div>
        <div className="text-xs font-semibold mb-2">Time Granularity</div>
        <Select
          buttonClassName="w-36"
          size="md"
          options={TimeGranularityV2.reactSelectOptions}
          value={timeGranularity}
          onChange={(val) => {
            if (val) {
              onChangeTimeGranularity(val);

              if (val === "hourly") {
                onChangeDateRange("last7Days");
                onChangeCompareDateRange({ value: "previousPeriod" });
              }
            }
          }}
        />
      </div>
    </div>
  );
};
